/**
 * Video Carousel Section JavaScript
 * Supports YouTube and Vimeo videos
 */

(function() {
  'use strict';

  function initVideoCarousel(section) {
    const track = section.querySelector('[data-carousel-track]');
    const prevBtn = section.querySelector('[data-carousel-prev]');
    const nextBtn = section.querySelector('[data-carousel-next]');
    const pagination = section.querySelector('[data-carousel-pagination]');
    const slides = track ? track.querySelectorAll('.video-carousel__slide') : [];

    if (!track || slides.length === 0) return;

    let currentIndex = 0;
    let slidesPerView = parseInt(track.dataset.slidesDesktop) || 3;
    const gap = parseInt(track.dataset.gap) || 20;

    function updateSlidesPerView() {
      const width = window.innerWidth;
      if (width < 750) {
        slidesPerView = parseInt(track.dataset.slidesMobile) || 1;
      } else if (width < 990) {
        slidesPerView = parseInt(track.dataset.slidesTablet) || 2;
      } else {
        slidesPerView = parseInt(track.dataset.slidesDesktop) || 3;
      }
    }

    function getMaxIndex() {
      return Math.max(0, slides.length - slidesPerView);
    }

    function updateCarousel() {
      if (slides.length === 0) return;

      const slideWidth = slides[0].offsetWidth + gap;
      const offset = currentIndex * slideWidth;
      track.style.transform = `translateX(-${offset}px)`;

      // Update navigation buttons
      if (prevBtn) prevBtn.disabled = currentIndex === 0;
      if (nextBtn) nextBtn.disabled = currentIndex >= getMaxIndex();

      // Update pagination
      if (pagination) {
        const dots = pagination.querySelectorAll('.video-carousel__dot');
        dots.forEach((dot, i) => {
          dot.classList.toggle('active', i === currentIndex);
        });
      }
    }

    function createPagination() {
      if (!pagination) return;
      pagination.innerHTML = '';

      const totalDots = getMaxIndex() + 1;
      for (let i = 0; i < totalDots; i++) {
        const dot = document.createElement('button');
        dot.className = 'video-carousel__dot' + (i === 0 ? ' active' : '');
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.addEventListener('click', () => {
          currentIndex = i;
          updateCarousel();
        });
        pagination.appendChild(dot);
      }
    }

    function handlePrev() {
      if (currentIndex > 0) {
        currentIndex--;
        updateCarousel();
      }
    }

    function handleNext() {
      if (currentIndex < getMaxIndex()) {
        currentIndex++;
        updateCarousel();
      }
    }

    // Fetch Vimeo thumbnails
    const vimeoThumbnails = section.querySelectorAll('.video-carousel__thumbnail--vimeo');
    vimeoThumbnails.forEach(img => {
      const vimeoId = img.dataset.vimeoId;
      if (vimeoId) {
        fetch(`https://vimeo.com/api/v2/video/${vimeoId}.json`)
          .then(res => res.json())
          .then(data => {
            if (data && data[0] && data[0].thumbnail_large) {
              img.src = data[0].thumbnail_large.replace('_640', '_960');
            }
          })
          .catch(() => {
            // Fallback - try the oembed API
            fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${vimeoId}`)
              .then(res => res.json())
              .then(data => {
                if (data && data.thumbnail_url) {
                  img.src = data.thumbnail_url;
                }
              })
              .catch(() => {});
          });
      }
    });

    // Video click handler
    const videoCards = section.querySelectorAll('.video-carousel__video-card');
    videoCards.forEach(card => {
      card.addEventListener('click', function() {
        const videoId = this.dataset.videoId;
        const platform = this.dataset.videoPlatform;
        if (!videoId || !platform) return;

        const thumbnailWrapper = this.querySelector('.video-carousel__thumbnail-wrapper');
        if (!thumbnailWrapper) return;

        const iframe = document.createElement('iframe');
        iframe.className = 'video-carousel__iframe';
        
        if (platform === 'youtube') {
          iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
          iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
        } else if (platform === 'vimeo') {
          iframe.src = `https://player.vimeo.com/video/${videoId}?autoplay=1`;
          iframe.allow = 'autoplay; fullscreen; picture-in-picture';
        }
        
        iframe.allowFullscreen = true;
        iframe.setAttribute('frameborder', '0');

        thumbnailWrapper.innerHTML = '';
        thumbnailWrapper.appendChild(iframe);
        thumbnailWrapper.classList.add('video-carousel__thumbnail-wrapper--playing');
      });
    });

    // Event listeners
    if (prevBtn) prevBtn.addEventListener('click', handlePrev);
    if (nextBtn) nextBtn.addEventListener('click', handleNext);

    // Touch/swipe support
    let touchStartX = 0;
    let touchEndX = 0;
    let touchStartY = 0;
    let isDragging = false;

    track.addEventListener('touchstart', e => {
      touchStartX = e.changedTouches[0].screenX;
      touchStartY = e.changedTouches[0].screenY;
      isDragging = true;
      track.style.transition = 'none';
    }, { passive: true });

    track.addEventListener('touchmove', e => {
      if (!isDragging) return;
      const touchCurrentX = e.changedTouches[0].screenX;
      const touchCurrentY = e.changedTouches[0].screenY;
      const diffX = touchStartX - touchCurrentX;
      const diffY = Math.abs(touchStartY - touchCurrentY);
      
      // Only handle horizontal swipes, allow vertical scroll
      if (Math.abs(diffX) > diffY) {
        const slideWidth = slides[0].offsetWidth + gap;
        const baseOffset = currentIndex * slideWidth;
        
        // Calculate max offset to prevent over-scrolling
        const trackWrapper = track.parentElement;
        const trackScrollWidth = track.scrollWidth;
        const visibleWidth = trackWrapper.offsetWidth;
        const maxOffset = Math.max(0, trackScrollWidth - visibleWidth);
        
        // Clamp the offset
        let newOffset = baseOffset + diffX;
        newOffset = Math.max(0, Math.min(newOffset, maxOffset));
        
        track.style.transform = `translateX(-${newOffset}px)`;
      }
    }, { passive: true });

    track.addEventListener('touchend', e => {
      if (!isDragging) return;
      isDragging = false;
      track.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.1, 0.25, 1)';
      
      touchEndX = e.changedTouches[0].screenX;
      const diff = touchStartX - touchEndX;
      
      if (Math.abs(diff) > 50) {
        if (diff > 0) handleNext();
        else handlePrev();
      } else {
        updateCarousel();
      }
    }, { passive: true });

    // Keyboard navigation
    section.addEventListener('keydown', e => {
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    });

    // Initialize
    updateSlidesPerView();
    createPagination();
    updateCarousel();

    // Resize handler (scoped to this instance)
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        updateSlidesPerView();
        currentIndex = Math.min(currentIndex, getMaxIndex());
        createPagination();
        updateCarousel();
      }, 150);
    });
  }

  // Initialize all video carousels on the page
  function init() {
    document.querySelectorAll('.video-carousel-section').forEach(section => {
      initVideoCarousel(section);
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Support Shopify theme editor - reinitialize on section load
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(event) {
      const section = event.target.querySelector('.video-carousel-section');
      if (section) {
        initVideoCarousel(section);
      }
    });
  }
})();
